import SiteCoreLinks from "@/components/SiteCoreLinks";
// EN mirror of /trust — credentials + acceptance proof
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { ShieldCheck, Award, FileCheck2, Users, Star, Building2 } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Trust & Credentials — Licensed Notary, MFA-Accepted Translation | NYC Language Institute";
const DESC =
  "Why clients trust NYC Legal: 6 licensed notarial attorneys and sworn translators, registered with the DBD. Send your documents for a free eligibility check.";

export const Route = createFileRoute("/en/trust")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/trust", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/trust", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/trust", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/trust", "en")},
      { property: "og:url", content: `${SITE}/en/trust` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/trust` },
      ...hreflangLinks("/trust", "/en/trust"),
    ],
    scripts: [speakableSchema(), {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LegalService",
        name: "NYC Legal & Notary Service Co., Ltd.",
        url: `${SITE}/en/trust`,
        inLanguage: "en",
        address: {
          "@type": "PostalAddress",
          streetAddress: "61 Soi Ladprao 95 (Prangthip), Wang Thonglang",
          addressLocality: "Bangkok",
          postalCode: "10310",
          addressCountry: "TH",
        },
      }),
    }],
  }),
  component: EnTrustPage,
});

const PILLARS = [
  { icon: Award, t: "6 court-licensed notarial attorneys", d: "All registered with the Lawyers Council of Thailand (สภาทนายความ) under MoJ supervision." },
  { icon: FileCheck2, t: "Embassy & MFA acceptance record", d: "Output accepted by 80+ embassies, Thai MFA Consular Dept., and courts in 77 provinces." },
  { icon: ShieldCheck, t: "Re-do guarantee", d: "If the receiving authority rejects on our error, we re-issue free of charge." },
  { icon: Users, t: "Individuals and institutions", d: "Individuals, law firms, hospitals, banks, corporate expansions and government tenders." },
  { icon: Star, t: "50+ languages", d: "Native-speaker translators, sworn and certified output accepted by embassies." },
  { icon: Building2, t: "18+ years in Bangkok", d: "Ladprao HQ since 2007, additional collection points across BTS/MRT lines." },
];

function EnTrustPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Trust built one <span className="text-amber-300">accepted document</span> at a time
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            We invest in licensure, paper trails and bilingual QA so your translation, notarisation or apostille is
            accepted on first submission. Below are the six pillars that back every quote we write.
          </p>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p) => (
            <article key={p.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{p.t}</h2>
              <p className="mt-2 text-sm text-slate-600">{p.d}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          Read the full credential dossier (Thai) on our{" "}
          <Link to="/trust" className="font-bold text-amber-700 hover:underline">/trust page</Link>{" "}
          or contact us at{" "}
          <Link to="/en/contact" className="font-bold text-amber-700 hover:underline">/en/contact</Link>.
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
